import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LayoutDashboard, X } from "lucide-react";

/**
 * Hidden route back into the admin — invisible to patients and to Google.
 *
 * Revealed either by visiting any page with ?admin=1 (remembered on that device,
 * and the parameter is stripped straight out of the URL) or by three quick taps
 * in the bottom-left corner. Taps that land on a link or button are ignored, so
 * the gesture can never be confused with tapping a real call-to-action.
 */
const CORNER = 64;
const WINDOW_MS = 1500;
const TAPS = 3;
const KEY = "admin_toggle_revealed";
const GAP = 12;
const BASE_OFFSET = 16;

// The Mini Consult launcher also lives bottom-left on a few pages.
const CORNER_NEIGHBOUR = '[aria-label="Mini Consult NOW"]';
const INTERACTIVE = 'a, button, input, select, textarea, label, [role="button"]';

const readFlag = () => {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
};

const AdminToggle = () => {
  const { pathname } = useLocation();
  const [revealed, setRevealed] = useState(readFlag);
  const [offset, setOffset] = useState(BASE_OFFSET);
  const taps = useRef([]);

  const hidden = pathname.startsWith("/admin") || pathname.startsWith("/cv/");

  const reveal = useCallback(() => {
    try {
      localStorage.setItem(KEY, "1");
    } catch {
      /* private mode — reveal for this view only */
    }
    setRevealed(true);
  }, []);

  // ?admin=1 — the reliable way in on any device
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("admin") !== "1") return;
    reveal();
    params.delete("admin");
    const qs = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
  }, [pathname, reveal]);

  // three taps in the bottom-left corner, ignoring taps on anything clickable
  useEffect(() => {
    if (revealed) return undefined;
    const onPointerDown = (e) => {
      if (e.clientX > CORNER || e.clientY < window.innerHeight - CORNER) return;
      if (e.target instanceof Element && e.target.closest(INTERACTIVE)) return;
      const now = Date.now();
      taps.current = [...taps.current.filter((t) => now - t < WINDOW_MS), now];
      if (taps.current.length >= TAPS) {
        taps.current = [];
        reveal();
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [revealed, reveal]);

  const measure = useCallback(() => {
    const neighbour = document.querySelector(CORNER_NEIGHBOUR);
    if (!neighbour) return setOffset(BASE_OFFSET);
    const r = neighbour.getBoundingClientRect();
    const inCorner = r.width > 0 && r.left < window.innerWidth * 0.4 && r.bottom > window.innerHeight - 220;
    return setOffset(inCorner ? Math.round(window.innerHeight - r.top + GAP) : BASE_OFFSET);
  }, []);

  useEffect(() => {
    if (!revealed || hidden) return undefined;
    measure();
    const late = setTimeout(measure, 900); // corner buttons live inside lazily-rendered pages
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(late);
      window.removeEventListener("resize", measure);
    };
  }, [revealed, hidden, pathname, measure]);

  if (!revealed || hidden) return null;

  const target = localStorage.getItem("admin_token") ? "/admin/dashboard" : "/admin/login";

  return (
    <div
      className="fixed left-4 z-[260] flex items-center gap-1 animate-[trustRise_0.4s_cubic-bezier(0.22,1,0.36,1)]"
      style={{ bottom: `max(${offset}px, env(safe-area-inset-bottom))` }}
      data-testid="admin-toggle"
    >
      <Link
        to={target}
        data-testid="admin-toggle-link"
        className="flex items-center gap-2 rounded-full bg-rabbit/95 px-4 py-2.5 text-xs font-semibold text-white shadow-lg backdrop-blur-sm transition-colors duration-200 hover:bg-[#006A71]"
      >
        <LayoutDashboard className="h-4 w-4" strokeWidth={1.8} />
        <span>Admin</span>
      </Link>
      <button
        type="button"
        aria-label="Hide admin shortcut"
        data-testid="admin-toggle-dismiss"
        onClick={() => {
          try {
            localStorage.removeItem(KEY);
          } catch {
            /* ignore */
          }
          setRevealed(false);
        }}
        className="flex h-7 w-7 items-center justify-center rounded-full bg-rabbit/80 text-white/70 shadow-md transition-colors duration-200 hover:text-white"
      >
        <X className="h-3.5 w-3.5" strokeWidth={2} />
      </button>
    </div>
  );
};

export default AdminToggle;
